import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  booleanAttribute,
  input,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BrnFieldControl } from '@spartan-ng/brain/field';
import { HlmDatePicker } from '../helm/date-picker/src/lib/hlm-date-picker';

let nextDatePickerId = 0;

@Component({
  selector: 'app-ui-date-picker',
  templateUrl: './date-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiDatePickerComponent),
      multi: true,
    },
  ],
  imports: [BrnFieldControl, HlmDatePicker],
  host: {
    class: 'block w-full min-w-0',
  },
})
export class UiDatePickerComponent implements ControlValueAccessor {
  readonly inputId = input(`app-ui-date-picker-${nextDatePickerId++}`);
  readonly required = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly valueChange = output<string | null>();

  readonly dateValue = signal<Date | undefined>(undefined);

  private readonly disabledFromControl = signal(false);
  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  readonly formatDate = (date: Date): string =>
    new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);

  get controlDisabled(): boolean {
    return this.disabled() || this.disabledFromControl();
  }

  handleDateChange(date: Date): void {
    this.dateValue.set(date);
    const value = this.formatModelDate(date);
    this.valueChange.emit(value);
    this.onChange(value);
    this.onTouched();
  }

  writeValue(value: string | null | undefined): void {
    this.dateValue.set(value ? this.parseModelDate(value) : undefined);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabledFromControl.set(disabled);
  }

  private parseModelDate(value: string): Date | undefined {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;

    const [year, month, day] = value.split('-').map(part => Number(part));
    if (!year || !month || !day) return undefined;

    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return undefined;

    return date;
  }

  private formatModelDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
